import { useNavigate } from 'react-router-dom';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useToaster } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';
import { createCountStore } from '@stores/create-count-store';

import { useCurrentCompany } from 'api/authentication/hooks';
import { useInvalidateFavorites } from 'api/favorites/hooks';
import { useOrderedQueryResults } from 'api/helpers';
import { useWebSocket, waitForElasticRefresh } from 'api/hooks';

import {
  WorkspaceData,
  createWorkspace,
  deleteWorkspace,
  getWorkspaceJourneysCount,
  loadWorkspace,
  loadWorkspaces,
  updateWorkspace,
} from './requests';

const workspacesQueryKey = ['workspaces'];

export const useInvalidateWorkspaces = () => {
  const queryClient = useQueryClient();
  const invalidateWorkspaces = async (workspaceId?: string) => {
    await Promise.all([
      queryClient.invalidateQueries(workspacesQueryKey),
      ...(workspaceId ? [queryClient.refetchQueries(workspacesQueryKey.concat(workspaceId))] : []),
    ]);
  };
  return { invalidateWorkspaces };
};

export const useWorkspaceUpdates = () => {
  const company = useCurrentCompany();
  const { invalidateWorkspaces } = useInvalidateWorkspaces();

  useWebSocket(`workspaces/${company?.shortId}/`, () => invalidateWorkspaces(), {
    enabled: Boolean(company?.shortId),
    throttleTime: 0,
  });
};

const cacheStore = createCountStore();

export const useCachedWorkspaces = () => {
  cacheStore.useStore();
  const queryClient = useQueryClient();
  const data = queryClient.getQueryData(workspacesQueryKey) as ReturnType<
    typeof useWorkspaces
  >['data'];

  return data ?? [];
};

export const useWorkspaces = () => {
  return useQuery({
    queryKey: workspacesQueryKey,
    queryFn: async () => {
      const workspaces = await loadWorkspaces();
      setTimeout(cacheStore.increase);
      return workspaces;
    },
  });
};

export const useWorkspacesOrdered = () => {
  const orderedWorkspaces = useOrderedQueryResults({
    userOptionKey: 'navbarWorkspacesOrder',
    useQueryHook: useWorkspaces,
  });

  return orderedWorkspaces;
};

export const useWorkspace = (workspaceId: string) => {
  return useQuery({
    queryKey: ['workspaces', workspaceId],
    queryFn: () => loadWorkspace(workspaceId),
    enabled: Boolean(workspaceId),
  });
};

export const useCreateWorkspace = () => {
  const { invalidateWorkspaces } = useInvalidateWorkspaces();
  const { showSuccess } = useToaster();
  const { t } = useScopedTranslate(locale);
  const navigate = useNavigate();
  return useMutation({
    mutationFn: createWorkspace,
    onSuccess: async (newWorkspace) => {
      await waitForElasticRefresh(invalidateWorkspaces);
      navigate(`/workspaces/${newWorkspace.shortId}/journeys`);
      showSuccess(t('workspaceCreated'));
    },
  });
};

export const useUpdateWorkspace = () => {
  const { invalidateWorkspaces } = useInvalidateWorkspaces();
  const queryClient = useQueryClient();
  const { showSuccess } = useToaster();
  const { t } = useScopedTranslate(locale);
  return useMutation({
    mutationFn: ({ shortId, data }: { shortId: string; data: WorkspaceData }) =>
      updateWorkspace(shortId, data),
    onSuccess: (_, variables) => {
      showSuccess(t('workspaceUpdated'));
      waitForElasticRefresh(() => {
        invalidateWorkspaces(variables.shortId);
        queryClient.invalidateQueries(['journey']);
        queryClient.invalidateQueries(['journeys']);
      });
    },
  });
};

export const useDeleteWorkspace = () => {
  const { invalidateWorkspaces } = useInvalidateWorkspaces();
  const { invalidateFavorites } = useInvalidateFavorites();
  const { showSuccess } = useToaster();
  const { t } = useScopedTranslate(locale);
  return useMutation({
    mutationFn: deleteWorkspace,
    onSuccess: () => {
      showSuccess(t('workspaceDeleted'));
      waitForElasticRefresh(invalidateWorkspaces);
      invalidateFavorites();
    },
  });
};

export const useUserWorkspaces = (userId: string) => {
  const workspaces = useWorkspacesOrdered();
  return (
    workspaces?.filter((workspace) => {
      return !workspace.isPrivate || workspace.members.includes(userId);
    }) ?? []
  );
};

export const useWorkspaceJourneysCount = (workspaceId: string) => {
  return useQuery({
    queryKey: ['workspaceJourneysCount', workspaceId],
    queryFn: async () => {
      try {
        return await getWorkspaceJourneysCount(workspaceId);
      } catch (error) {
        return 0;
      }
    },
    enabled: Boolean(workspaceId),
  });
};

const locale = {
  en: {
    workspaceCreated: 'Workspace created',
    workspaceUpdated: 'Workspace updated',
    workspaceDeleted: 'Workspace deleted',
  },
};
